import { Form, Head, Link } from "@inertiajs/react"
import { ChevronRight, Clock, LogIn, Trophy, Users } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Divider } from "@/components/divider"
import { Heading } from "@/components/heading"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { newLeagueTeamPath, performJoinLeaguePath } from "@/routes"
import type { BreadcrumbItem, JoinLeagueProps } from "@/types"

function seasonLabel(status: string | null): string {
  if (!status) return "No Season Yet"
  if (status === "draft") return "Pre-Season Draft"
  if (status === "active") return "Season Active"
  return "Season Completed"
}

type Phase = "invite" | "slamming" | "success"

export default function JoinLeague({
  league,
  passphrase,
  joined,
}: JoinLeagueProps) {
  const [phase, setPhase] = useState<Phase>("invite")

  // Refs for staggered success animations
  const trophyRef = useRef<HTMLDivElement>(null)
  const labelRef = useRef<HTMLParagraphElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const dividerRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLParagraphElement>(null)
  const ctaRef = useRef<HTMLAnchorElement>(null)

  const breadcrumbs: BreadcrumbItem[] = [{ title: "League Invitation" }]

  // Trigger wipe transition when joined prop becomes true
  useEffect(() => {
    if (!joined) return

    // Defer setState to avoid calling it synchronously within effect body
    const slamTimer = setTimeout(() => setPhase("slamming"), 0)
    // Phase 2: swap to success state after slam completes (0.35s) + small buffer
    const partTimer = setTimeout(() => setPhase("success"), 500)

    return () => {
      clearTimeout(slamTimer)
      clearTimeout(partTimer)
    }
  }, [joined])

  // Staggered success animations after phase becomes "success"
  useEffect(() => {
    if (phase !== "success") return

    const timers: ReturnType<typeof setTimeout>[] = []

    timers.push(
      setTimeout(() => {
        if (trophyRef.current) {
          trophyRef.current.style.opacity = "1"
          trophyRef.current.style.animation =
            "success-scale-in 0.5s cubic-bezier(0.25, 1.15, 0.5, 1) forwards, trophy-pulse 2s ease-in-out infinite"
        }
      }, 200),
    )

    timers.push(
      setTimeout(() => {
        if (labelRef.current) {
          labelRef.current.style.opacity = "1"
          labelRef.current.style.animation =
            "success-fade-up 0.4s ease-out forwards"
        }
      }, 350),
    )

    timers.push(
      setTimeout(() => {
        if (headingRef.current) {
          headingRef.current.style.opacity = "1"
          headingRef.current.style.animation =
            "success-scale-in 0.5s cubic-bezier(0.25, 1.15, 0.5, 1) forwards"
        }
      }, 450),
    )

    timers.push(
      setTimeout(() => {
        if (dividerRef.current) {
          dividerRef.current.style.opacity = "1"
          dividerRef.current.style.animation =
            "success-fade-up 0.4s ease-out forwards"
        }
      }, 600),
    )

    timers.push(
      setTimeout(() => {
        if (textRef.current) {
          textRef.current.style.opacity = "1"
          textRef.current.style.animation =
            "success-fade-up 0.4s ease-out forwards"
        }
      }, 700),
    )

    timers.push(
      setTimeout(() => {
        if (ctaRef.current) {
          ctaRef.current.style.opacity = "1"
          ctaRef.current.style.animation =
            "success-fade-up 0.4s ease-out forwards"
        }
      }, 850),
    )

    return () => {
      timers.forEach(clearTimeout)
    }
  }, [phase])

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="League Invitation" />

      <div
        className="mx-auto flex w-full max-w-7xl items-center justify-center px-4 py-8"
        style={{ minHeight: "calc(100vh - 16rem)" }}
      >
        {/* Invite state */}
        {phase !== "success" && (
          <Card className="flex w-full max-w-lg flex-col items-center px-8 py-12 text-center">
            {/* Trophy in shield */}
            <div
              className="shield mb-6 flex h-24 w-20 items-start justify-center pt-4"
              style={{
                background:
                  "linear-gradient(180deg, oklch(0.30 0.08 55), oklch(0.18 0.04 55))",
                border: "2px solid var(--border-heavy)",
              }}
            >
              <Trophy className="text-accent mt-1 size-10" />
            </div>

            {/* Headline */}
            <p className="text-accent-dim mb-2 text-xs font-bold tracking-widest uppercase">
              You Have Been Summoned
            </p>
            <Heading level={1} wrapperClassName="mb-0" className="text-5xl">
              {league.name}
            </Heading>

            {/* Divider */}
            <Divider className="mt-5 mb-5 w-full" />

            {/* Context */}
            <p className="text-muted-foreground max-w-xs text-sm">
              <span className="text-foreground font-semibold">
                Coach {league.creator_name}
              </span>{" "}
              has invited you to join the league. Glory and broken bones await.
            </p>

            {/* League info badges */}
            <div className="text-muted-foreground mt-5 flex items-center justify-center gap-6 text-xs font-semibold tracking-wide uppercase">
              <span className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                {league.coach_count === 1
                  ? "1 coach"
                  : `${league.coach_count} coaches`}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {seasonLabel(league.season_status)}
              </span>
            </div>

            {/* CTA form */}
            <Form
              method="post"
              action={performJoinLeaguePath(passphrase)}
              className="mt-8"
            >
              {({ processing: formProcessing }) => (
                <Button
                  type="submit"
                  size="lg"
                  className="h-12 gap-2.5 px-8 text-base tracking-widest"
                  disabled={formProcessing}
                >
                  <LogIn className="size-5" />
                  {formProcessing ? "Joining…" : "Join the Fray"}
                </Button>
              )}
            </Form>

            <p className="text-muted-foreground mt-4 text-xs">
              By joining you agree to Nuffle&apos;s divine judgement
            </p>
          </Card>
        )}

        {/* Success state */}
        {phase === "success" && (
          <div className="flex w-full max-w-lg flex-col items-center px-8 py-12 text-center">
            {/* Animated trophy shield */}
            <div
              ref={trophyRef}
              className="shield mb-6 flex h-28 w-24 items-start justify-center pt-5"
              style={{
                background:
                  "linear-gradient(180deg, oklch(0.38 0.14 85), oklch(0.22 0.08 55))",
                border: "3px solid var(--accent)",
                opacity: 0,
              }}
            >
              <Trophy className="text-accent mt-1 size-12" />
            </div>

            <p
              ref={labelRef}
              className="text-accent-dim mb-2 text-xs font-bold tracking-widest uppercase"
              style={{ opacity: 0 }}
            >
              Welcome, Coach
            </p>
            <h1
              ref={headingRef}
              className="font-display text-gold text-5xl leading-none font-bold"
              style={{ opacity: 0 }}
            >
              You&apos;re In
            </h1>

            <div
              ref={dividerRef}
              className="mt-5 mb-5 flex w-full items-center justify-center gap-2"
              style={{ opacity: 0 }}
            >
              <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[var(--border-heavy)]" />
              <span className="text-accent-dim">◆</span>
              <span className="h-px flex-1 bg-gradient-to-r from-[var(--border-heavy)] to-transparent" />
            </div>

            <p
              ref={textRef}
              className="text-muted-foreground max-w-xs text-sm"
              style={{ opacity: 0 }}
            >
              You have joined{" "}
              <span className="text-foreground font-semibold">
                {league.name}
              </span>
              . Pick a race and build your team to begin.
            </p>

            {league.id && (
              <Button
                size="lg"
                className="mt-8 h-12 gap-2.5 px-8 text-base tracking-widest"
                style={{ opacity: 0 }}
                asChild
              >
                <Link ref={ctaRef} href={newLeagueTeamPath(league.id)}>
                  <ChevronRight className="size-5" />
                  Draft Your Team
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Screen wipe overlay */}
      <div className="pointer-events-none fixed inset-0 z-[10000] overflow-hidden">
        {/* Top plate */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "52%",
            background: "var(--card)",
            borderBottom: "3px solid var(--border-heavy)",
            boxShadow:
              "inset 0 1px 0 oklch(0.28 0.02 70 / 0.5), 0 4px 20px oklch(0 0 0 / 0.8)",
            transform: "translateY(-105%)",
            animation:
              phase === "slamming"
                ? "slam-top 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards"
                : phase === "success"
                  ? "part-top 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards"
                  : undefined,
          }}
        >
          {/* Hazard stripe bar (replaces ::after) */}
          <span
            className="absolute right-0 bottom-0 left-0 h-3"
            style={{
              background:
                "repeating-linear-gradient(-45deg, oklch(0.5 0.2 85) 0px, oklch(0.5 0.2 85) 6px, oklch(0.2 0.02 55) 6px, oklch(0.2 0.02 55) 12px)",
            }}
          />
          {/* Rivets */}
          <span
            className="absolute rounded-full"
            style={{
              bottom: 20,
              left: 24,
              width: 10,
              height: 10,
              background:
                "radial-gradient(circle at 35% 35%, oklch(0.5 0.06 70), oklch(0.22 0.02 55))",
              boxShadow:
                "inset 0 1px 2px oklch(0.6 0.05 70 / 0.5), 0 1px 3px oklch(0 0 0 / 0.5)",
            }}
          />
          <span
            className="absolute rounded-full"
            style={{
              bottom: 20,
              right: 24,
              width: 10,
              height: 10,
              background:
                "radial-gradient(circle at 35% 35%, oklch(0.5 0.06 70), oklch(0.22 0.02 55))",
              boxShadow:
                "inset 0 1px 2px oklch(0.6 0.05 70 / 0.5), 0 1px 3px oklch(0 0 0 / 0.5)",
            }}
          />
        </div>

        {/* Bottom plate */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "52%",
            background: "var(--card)",
            borderTop: "3px solid var(--border-heavy)",
            boxShadow:
              "inset 0 -1px 0 oklch(0.1 0.01 50 / 0.8), 0 -4px 20px oklch(0 0 0 / 0.8)",
            transform: "translateY(105%)",
            animation:
              phase === "slamming"
                ? "slam-bottom 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards"
                : phase === "success"
                  ? "part-bottom 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards"
                  : undefined,
          }}
        >
          {/* Hazard stripe bar (replaces ::after) */}
          <span
            className="absolute top-0 right-0 left-0 h-3"
            style={{
              background:
                "repeating-linear-gradient(-45deg, oklch(0.5 0.2 85) 0px, oklch(0.5 0.2 85) 6px, oklch(0.2 0.02 55) 6px, oklch(0.2 0.02 55) 12px)",
            }}
          />
          {/* Rivets */}
          <span
            className="absolute rounded-full"
            style={{
              top: 20,
              left: 24,
              width: 10,
              height: 10,
              background:
                "radial-gradient(circle at 35% 35%, oklch(0.5 0.06 70), oklch(0.22 0.02 55))",
              boxShadow:
                "inset 0 1px 2px oklch(0.6 0.05 70 / 0.5), 0 1px 3px oklch(0 0 0 / 0.5)",
            }}
          />
          <span
            className="absolute rounded-full"
            style={{
              top: 20,
              right: 24,
              width: 10,
              height: 10,
              background:
                "radial-gradient(circle at 35% 35%, oklch(0.5 0.06 70), oklch(0.22 0.02 55))",
              boxShadow:
                "inset 0 1px 2px oklch(0.6 0.05 70 / 0.5), 0 1px 3px oklch(0 0 0 / 0.5)",
            }}
          />
        </div>
      </div>
    </AppLayout>
  )
}
